import { rgb, type RGB } from 'pdf-lib'
import type { ImeiReportPdfLabels } from './imeiReportPdf'
import type { PDFDocument, PDFFont, PDFPage } from 'pdf-lib'

type PdfEmbeddedImage = Awaited<ReturnType<PDFDocument['embedPng']>>

export type PdfIconWriter = {
  page: PDFPage
  font: PDFFont
  fontBold: PDFFont
}

/** Matches web `.vr-check-row__icon-wrap` / verify report icons. */
const ICON_WRAP = rgb(0.867, 0.961, 0.969)
const ICON_STROKE = rgb(0.082, 0.616, 0.702)

type PathSpec = { d: string; mode: 'stroke' | 'fill' }

/** Same geometry as `VerifyReportIcons.tsx` (viewBox 0 0 24 24). */
const SECTION_ICON_PATHS: Record<string, PathSpec[]> = {
  imei: [
    {
      d: 'M9 2.5h6a2.5 2.5 0 0 1 2.5 2.5v14a2.5 2.5 0 0 1-2.5 2.5H9a2.5 2.5 0 0 1-2.5-2.5V5a2.5 2.5 0 0 1 2.5-2.5z',
      mode: 'stroke',
    },
    { d: 'M12 18.5m-0.75 0a0.75 0.75 0 1 0 1.5 0a0.75 0.75 0 1 0-1.5 0', mode: 'fill' },
  ],
  device: [
    { d: 'M12 11c1.66 0 3-1.34 3-3S13.66 5 12 5 9 6.34 9 8s1.34 3 3 3z', mode: 'stroke' },
    { d: 'M6.5 12.5c0-3.04 2.46-5.5 5.5-5.5M17.5 12.5c0 3.87-3.13 7-7 7', mode: 'stroke' },
  ],
  blacklist: [
    { d: 'M12 3 20 6v6c0 5-3.5 9.5-8 9-4.5-.5-8-4-8-9V6l8-3z', mode: 'stroke' },
    { d: 'm9 12 2 2 4-4', mode: 'stroke' },
  ],
  carrier: [
    { d: 'M4.5 8.5 12 16l7.5-7.5', mode: 'stroke' },
    { d: 'M7 12.5 12 17l5-5', mode: 'stroke' },
    { d: 'M12 19m-1.25 0a1.25 1.25 0 1 0 2.5 0a1.25 1.25 0 1 0-2.5 0', mode: 'fill' },
  ],
  lock: [
    { d: 'M5 11h14v10H5z', mode: 'stroke' },
    { d: 'M8 11V8a4 4 0 0 1 8 0v3', mode: 'stroke' },
  ],
  finance: [
    { d: 'M3 6h18v12H3z', mode: 'stroke' },
    { d: 'M3 10h18', mode: 'stroke' },
  ],
  mdm: [
    { d: 'M3 8h18v12H3z', mode: 'stroke' },
    { d: 'M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', mode: 'stroke' },
  ],
  android: [
    { d: 'M3 8h18v12H3z', mode: 'stroke' },
    { d: 'M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', mode: 'stroke' },
  ],
}

const CHECKMARK_PATH = 'M20 6 9 17l-5-5'

function drawViewBoxIcon(w: PdfIconWriter, cx: number, cy: number, paths: PathSpec[], iconPt = 15) {
  const scale = iconPt / 24
  const originX = cx - 12 * scale
  const originY = cy - 12 * scale
  const strokeW = 1.7 * scale

  for (const spec of paths) {
    if (spec.mode === 'fill') {
      w.page.drawSvgPath(spec.d, {
        x: originX,
        y: originY,
        scale,
        color: ICON_STROKE,
      })
    } else {
      w.page.drawSvgPath(spec.d, {
        x: originX,
        y: originY,
        scale,
        borderColor: ICON_STROKE,
        borderWidth: strokeW,
      })
    }
  }
}

export function drawPdfSectionIcon(
  w: PdfIconWriter,
  sectionId: string | undefined,
  cx: number,
  cy: number,
  iconImage?: PdfEmbeddedImage,
) {
  w.page.drawCircle({ x: cx, y: cy, size: 16, color: ICON_WRAP })
  const id = sectionId ?? 'imei'
  if (iconImage) {
    const size = 20
    w.page.drawImage(iconImage, {
      x: cx - size / 2,
      y: cy - size / 2,
      width: size,
      height: size,
    })
    return
  }
  const paths = SECTION_ICON_PATHS[id] ?? SECTION_ICON_PATHS.imei
  drawViewBoxIcon(w, cx, cy, paths)
}

export function verdictMarkColors(tone: ImeiReportPdfLabels['statusTone']): { fg: RGB; bg: RGB } {
  switch (tone) {
    case 'clean':
      return { fg: rgb(0.07, 0.53, 0.4), bg: rgb(0.906, 0.973, 0.953) }
    case 'attention':
      return { fg: rgb(0.65, 0.41, 0), bg: rgb(1, 0.96, 0.87) }
    case 'risk':
    case 'invalid':
      return { fg: rgb(0.77, 0.23, 0.27), bg: rgb(0.99, 0.92, 0.93) }
    default:
      return { fg: rgb(0.44, 0.51, 0.61), bg: rgb(0.93, 0.95, 0.96) }
  }
}

export function drawPdfVerdictIcon(
  w: PdfIconWriter,
  cx: number,
  cy: number,
  tone: ImeiReportPdfLabels['statusTone'],
  _unicode: boolean,
  resultHeadlineKey?: string,
) {
  const noIssues = resultHeadlineKey === 'vc.imeiReport.result.noIssuesFound'
  const markTone: ImeiReportPdfLabels['statusTone'] =
    noIssues && tone !== 'risk' && tone !== 'invalid' ? 'clean' : tone
  const { fg, bg } = verdictMarkColors(markTone)
  w.page.drawCircle({ x: cx, y: cy, size: 26, color: bg })

  if (!noIssues && (tone === 'attention' || tone === 'risk' || tone === 'invalid')) {
    w.page.drawText('!', {
      x: cx - 3.5,
      y: cy - 6,
      size: 20,
      font: w.fontBold,
      color: fg,
    })
    return
  }

  if (noIssues || tone === 'clean') {
    const scale = 22 / 24
    w.page.drawSvgPath(CHECKMARK_PATH, {
      x: cx - 12 * scale,
      y: cy - 12 * scale,
      scale,
      borderColor: fg,
      borderWidth: 2.2 * scale,
    })
    return
  }

  w.page.drawText('?', {
    x: cx - 3.5,
    y: cy - 5,
    size: 16,
    font: w.fontBold,
    color: fg,
  })
}

export function drawPdfTrustCheck(w: PdfIconWriter, baselineX: number, baselineY: number) {
  const scale = 11 / 24
  const cx = baselineX + 5
  const cy = baselineY + 4
  w.page.drawSvgPath(CHECKMARK_PATH, {
    x: cx - 12 * scale,
    y: cy - 12 * scale,
    scale,
    borderColor: rgb(0.098, 0.663, 0.471),
    borderWidth: 2.5 * scale,
  })
}
